import React, { useEffect } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { PieChart as RePieChart, Pie, Cell } from "recharts";

interface PredictionModalProps {
  isOpen: boolean;
  onClose: () => void;
  predictionResult: {
    prediction: boolean;
    accuracy: number;
  };
}

const RADIAN = Math.PI / 180;
const data = [
  { name: "High Risk", value: 34, color: "#00cc44" },
  { name: "Medium Risk", value: 33, color: "#ffcc00" },
  { name: "Low Risk", value: 33, color: "#cc0000" },
];

const calculateDimensions = (containerWidth: number) => {
  const width = Math.min(280, containerWidth);
  const height = width * 0.5;
  const cx = width * 0.5;
  const cy = height * 0.9;
  const outerRadius = width * 0.4;
  const innerRadius = outerRadius * 0.6;

  return { width, height, cx, cy, outerRadius, innerRadius };
};

const needle = (
  value: number,
  data: any[],
  cx: number,
  cy: number,
  iR: number,
  oR: number,
  color: string
) => {
  let total = data.reduce((sum, entry) => sum + entry.value, 0);
  const angle = 180 * (1 - value / 100);
  const length = (iR + 2 * oR) / 3;
  const sin = Math.sin(-RADIAN * angle);
  const cos = Math.cos(-RADIAN * angle);
  const r = 4;
  const x0 = cx;
  const y0 = cy;
  const xba = x0 + r * sin;
  const yba = y0 - r * cos;
  const xbb = x0 - r * sin;
  const ybb = y0 + r * cos;
  const xp = x0 + length * cos;
  const yp = y0 + length * sin;

  return [
    <circle key="needle-base" cx={x0} cy={y0} r={r} fill={color} />,
    <path
      key="needle-arm"
      d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`}
      fill={color}
    />,
  ];
};

const PredictionModal: React.FC<PredictionModalProps> = ({
  isOpen,
  onClose,
  predictionResult,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = React.useState(calculateDimensions(280));

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions(calculateDimensions(containerRef.current.offsetWidth));
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      <Modal
        backdrop="static"
        centered={true}
        toggle={onClose}
        isOpen={isOpen}
        className="rv-modal"
      >
        <ModalHeader className="w-100 justify-content-center p-13 position-relative rv-breadcrumb-border ">
          Inference
          <button
            type="button"
            className="close-button-currency position-absolute top-0 end-0 mt-1 me-3 border-0 bg-transparent fs-3"
            onClick={onClose}
          >
            &times;
          </button>
        </ModalHeader>
        <ModalBody className="fs-5 fw-bold text-center d-block">
          <div className="d-flex flex-column align-items-center">
            <div className="fs-2 fw-bold mb-3">
              {predictionResult.prediction ? "True" : "False"}
            </div>

            <div
              ref={containerRef}
              className="w-100 d-flex justify-content-center"
              style={{ maxWidth: "360px" }}
            >
              <RePieChart width={dimensions.width} height={dimensions.height}>
                <Pie
                  dataKey="value"
                  startAngle={180}
                  endAngle={0}
                  data={data}
                  cx={dimensions.cx}
                  cy={dimensions.cy}
                  innerRadius={dimensions.innerRadius}
                  outerRadius={dimensions.outerRadius}
                  stroke="none"
                // cornerRadius={dimensions.outerRadius * 0.2}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                {needle(
                  predictionResult.accuracy,
                  data,
                  dimensions.cx,
                  dimensions.cy,
                  dimensions.innerRadius,
                  dimensions.outerRadius,
                  "#000"
                )}
              </RePieChart>
            </div>

            <div className="fs-1 fw-bold mt-3">
              {predictionResult.accuracy}%
            </div>
            <div className="text-sm text-secondary fw-normal text-muted">
              Inference Accuracy
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
};

export default PredictionModal;